import { useEffect, useMemo, useState } from 'react'
import { useKassaSocket } from '../lib/kassaSocket'
import {
  ShoppingBag, Plus, Minus, Trash2, Loader2, AlertCircle, CheckCircle2, Beer, Gift,
  Coffee, Utensils, Package,
} from 'lucide-react'
import { kassaAPI } from '@/lib/api'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import PageHeader from '@/components/PageHeader'

const CATEGORY_ICONS = {
  drank: Beer,
  frisdrank: Coffee,
  eten: Utensils,
  snack: Package,
}

const formatEuro = (cents) => `€${(cents / 100).toFixed(2)}`

function ItemCard({ item, quantity, onAdd, onRemove }) {
  const Icon = CATEGORY_ICONS[item.category] || Package
  const soldOut = item.is_sold_out
  const stock = item.stock
  const remaining = stock !== null && stock !== undefined ? Math.max(0, stock - quantity) : null
  const disabled = !item.enabled || soldOut
  const atLimit = remaining !== null && remaining <= 0 && quantity > 0
  const lowStock = stock !== null && stock !== undefined && stock > 0 && stock <= 5

  return (
    <Card className={`transition-colors ${soldOut ? 'opacity-60' : 'hover:border-acid'}`}>
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className={`h-10 w-10 rounded-lg flex items-center justify-center shrink-0 ${soldOut ? 'bg-muted' : 'bg-acid/10'}`}>
            <Icon className={`h-5 w-5 ${soldOut ? 'text-muted-foreground' : 'text-acid'}`} />
          </div>
          <div className="flex-1 min-w-0">
            <p className={`font-medium truncate ${soldOut ? 'line-through text-muted-foreground' : ''}`}>{item.name}</p>
            <div className="flex items-center gap-2 mt-0.5 flex-wrap">
              <span className={`text-sm font-semibold ${soldOut ? 'text-muted-foreground' : 'text-acid'}`}>{formatEuro(item.price_cents)}</span>
              {item.contains_alcohol && <Badge variant="outline" className="text-[10px]">alc</Badge>}
              {item.category && <span className="text-[10px] text-muted-foreground uppercase">{item.category}</span>}
              {soldOut && <Badge variant="destructive" className="text-[10px]">Uitverkocht</Badge>}
              {!soldOut && lowStock && <Badge variant="outline" className="text-[10px] text-yellow-500 border-yellow-500/40">nog {stock}</Badge>}
            </div>
          </div>
        </div>
        <div className="flex items-center justify-between mt-3">
          {quantity > 0 ? (
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" onClick={() => onRemove(item.id)} className="h-8 w-8 p-0">
                <Minus className="h-3.5 w-3.5" />
              </Button>
              <span className="min-w-[2ch] text-center font-medium">{quantity}</span>
              <Button size="sm" variant="outline" onClick={() => onAdd(item.id)} className="h-8 w-8 p-0" disabled={disabled || atLimit}>
                <Plus className="h-3.5 w-3.5" />
              </Button>
            </div>
          ) : (
            <div />
          )}
          <Button size="sm" onClick={() => onAdd(item.id)} disabled={disabled || atLimit}>
            {soldOut ? 'Uitverkocht' : atLimit ? 'Max bereikt' : quantity > 0 ? '+1' : 'Toevoegen'}
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

export default function BestellenPage() {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [menu, setMenu] = useState(null)
  const [account, setAccount] = useState(null)
  const [cart, setCart] = useState({})
  const [placing, setPlacing] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [lastResult, setLastResult] = useState(null)
  const [voucherCount, setVoucherCount] = useState(0)

  const loadAll = async () => {
    setLoading(true)
    setError(null)
    try {
      const [menuRes, accRes, voucherRes] = await Promise.allSettled([
        kassaAPI.getMenu(),
        kassaAPI.getMyAccount(),
        kassaAPI.getMyVouchers(),
      ])
      if (menuRes.status === 'fulfilled') setMenu(menuRes.value.data)
      else setError('Menu niet beschikbaar (' + (menuRes.reason?.response?.data?.error || 'onbekende fout') + ')')
      if (accRes.status === 'fulfilled') setAccount(accRes.value.data)
      if (voucherRes.status === 'fulfilled') setVoucherCount((voucherRes.value.data.vouchers || []).length)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { loadAll() }, [])
  useKassaSocket(() => { loadAll() })

  const items = menu?.items || []
  const activeEvent = menu?.activeEvent

  const grouped = useMemo(() => {
    const g = {}
    for (const it of items) {
      if (!it.enabled) continue
      const cat = it.category || 'overig'
      if (!g[cat]) g[cat] = []
      g[cat].push(it)
    }
    return g
  }, [items])

  const cartLines = useMemo(() => {
    const lines = []
    for (const [id, qty] of Object.entries(cart)) {
      if (qty <= 0) continue
      const item = items.find(i => String(i.id) === String(id))
      if (item) lines.push({ item, qty })
    }
    return lines
  }, [cart, items])

  const totalCents = cartLines.reduce((sum, l) => sum + l.item.price_cents * l.qty, 0)

  const addToCart = (id) => setCart(c => {
    const item = items.find(i => i.id === id)
    const currentQty = c[id] || 0
    if (item?.is_sold_out) return c
    if (item?.stock !== null && item?.stock !== undefined && currentQty >= item.stock) return c
    return { ...c, [id]: currentQty + 1 }
  })
  const removeFromCart = (id) => setCart(c => {
    const n = { ...c }
    const q = (n[id] || 0) - 1
    if (q <= 0) delete n[id]
    else n[id] = q
    return n
  })
  const clearCart = () => setCart({})

  const placeOrder = async () => {
    setPlacing(true)
    setLastResult(null)
    try {
      const payload = cartLines.map(l => ({ item_id: l.item.id, quantity: l.qty }))
      const res = await kassaAPI.placeOrder(payload)
      setLastResult({ ok: true, data: res.data })
      clearCart()
      loadAll()
    } catch (err) {
      const data = err.response?.data
      setLastResult({ ok: false, status: err.response?.status, data })
    } finally {
      setPlacing(false)
      setConfirmOpen(false)
    }
  }

  if (loading) {
    return (
      <div className="p-6 max-w-6xl mx-auto flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-neon-blue" />
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <PageHeader
        icon={ShoppingBag}
        title="Bestellen"
        subtitle={activeEvent ? `Event: ${activeEvent.name}` : 'Drank & snacks aan de bar afrekenen'}
        actions={
          <div className="flex items-center gap-2">
            {account?.linked ? (() => {
              const openCents = (account.accounts || []).reduce(
                (s, a) => s + (a.outstanding_cents ?? a.open_cents ?? a.balance_cents ?? a.total_cents ?? 0), 0)
              const openStr = `€${(openCents / 100).toFixed(2)}`
              return (
                <>
                  <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-neon-blue/40 bg-neon-blue/10 text-neon-blue text-sm font-semibold">
                    <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">openstaand</span>
                    <span className="tabular-nums">{openStr}</span>
                  </div>
                  {voucherCount > 0 && (
                    <Badge variant="outline" className="text-xs">{voucherCount} voucher{voucherCount === 1 ? '' : 's'}</Badge>
                  )}
                </>
              )
            })() : (
              <Badge variant="destructive" className="text-xs">Niet gekoppeld</Badge>
            )}
          </div>
        }
      />

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Kassa niet bereikbaar</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {!account?.linked && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Kassa nog niet gekoppeld</AlertTitle>
          <AlertDescription>
            Vraag een admin om je LAN-account aan je bar-kaart te koppelen. Je kan de menu bekijken maar niet bestellen.
          </AlertDescription>
        </Alert>
      )}

      {activeEvent && items.length === 0 && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Geen items in menu</AlertTitle>
          <AlertDescription>Er zijn nog geen items zichtbaar. Vraag de bar om het menu te activeren.</AlertDescription>
        </Alert>
      )}

      {lastResult && (
        <Alert variant={lastResult.ok ? 'default' : 'destructive'}>
          {lastResult.ok ? <CheckCircle2 className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
          <AlertTitle>{lastResult.ok ? 'Bestelling doorgestuurd' : 'Bestelling mislukt'}</AlertTitle>
          <AlertDescription>
            {lastResult.ok
              ? `Rekening ${lastResult.data?.upstream?.applied ? 'geboekt' : 'ontvangen (wacht op verwerking)'}. Referentie: ${lastResult.data?.externalId}`
              : (lastResult.data?.error || JSON.stringify(lastResult.data?.upstream?.queue || {}))}
          </AlertDescription>
        </Alert>
      )}

      {voucherCount > 0 && (
        <Alert>
          <Gift className="h-4 w-4" />
          <AlertTitle>Je hebt {voucherCount} actieve voucher{voucherCount !== 1 ? 's' : ''}</AlertTitle>
          <AlertDescription>
            Meld dit aan de bar bij het afrekenen om te gebruiken. (Vouchers activeren via de LAN-server is nog niet mogelijk — kassa API ondersteunt dit niet native.)
          </AlertDescription>
        </Alert>
      )}

      <div className="grid gap-4 lg:grid-cols-[1fr_320px]">
        <div className="space-y-6">
          {Object.entries(grouped).map(([cat, catItems]) => (
            <section key={cat}>
              <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-2">{cat}</h2>
              <div className="grid gap-3 sm:grid-cols-2 md:grid-cols-3">
                {catItems.map(item => (
                  <ItemCard key={item.id} item={item} quantity={cart[item.id] || 0} onAdd={addToCart} onRemove={removeFromCart} />
                ))}
              </div>
            </section>
          ))}
          {Object.keys(grouped).length === 0 && !error && items.length > 0 && (
            <p className="text-muted-foreground">Geen actieve items gevonden.</p>
          )}
        </div>

        <aside className="lg:sticky lg:top-4 h-fit">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ShoppingBag className="h-5 w-5" /> Winkelmandje ({cartLines.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {cartLines.length === 0 ? (
                <p className="text-sm text-muted-foreground">Nog niks gekozen.</p>
              ) : (
                <>
                  <ul className="space-y-2 max-h-80 overflow-y-auto">
                    {cartLines.map(l => (
                      <li key={l.item.id} className="flex items-center justify-between gap-2 text-sm">
                        <span className="truncate">{l.qty}× {l.item.name}</span>
                        <span className="text-muted-foreground shrink-0">{formatEuro(l.item.price_cents * l.qty)}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="pt-2 border-t border-border flex items-center justify-between font-semibold">
                    <span>Totaal</span>
                    <span className="text-neon-blue">{formatEuro(totalCents)}</span>
                  </div>
                  <div className="flex flex-col gap-2 pt-2">
                    <Button
                      onClick={() => setConfirmOpen(true)}
                      disabled={!account?.linked || placing || cartLines.length === 0}
                    >
                      Bestellen op rekening
                    </Button>
                    <Button variant="ghost" size="sm" onClick={clearCart} disabled={placing}>
                      <Trash2 className="h-3.5 w-3.5 mr-1" /> Leegmaken
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </aside>
      </div>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Bestelling bevestigen</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">
              Je bestelt {cartLines.length} item{cartLines.length !== 1 ? 's' : ''} voor <strong>{formatEuro(totalCents)}</strong>.
              Dit wordt op jouw rekening geplaatst en betaal je later aan de bar.
            </p>
            <ul className="text-sm border rounded-md p-3 space-y-1">
              {cartLines.map(l => (
                <li key={l.item.id} className="flex justify-between">
                  <span>{l.qty}× {l.item.name}</span>
                  <span>{formatEuro(l.item.price_cents * l.qty)}</span>
                </li>
              ))}
            </ul>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmOpen(false)} disabled={placing}>Annuleren</Button>
            <Button onClick={placeOrder} disabled={placing}>
              {placing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Bevestigen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
